import { For, Show } from 'solid-js';
import { DropdownOption, FieldType, FormFieldContext } from '../forms-types';
import { errorDescribedBy, FieldErrorMessage, fieldErrorId } from './primitives';
import { Checkbox } from '../../../ui-components/inputs/checkbox/checkbox';
import { Radio } from '../../../ui-components/inputs/radio/radio';
import { VerticalSpace } from '../../../ui-components/utility-style-components/spacing';
import { parseFieldOptions } from '../utils';

type FieldProps = {
    field: FieldType;
    ctx: FormFieldContext;
};

export const RadioButtons = (props: FieldProps) => {
    const { formId, formValues, errors, onRadioChange } = props.ctx;

    const extraSettings = () => props.field.extraSettings;
    const required = () => props.field.required;
    const eloquaName = () => extraSettings()?.name.value ?? '';
    const placeholder = () => required() ? extraSettings()!.placeholder.value + '*' : extraSettings()?.placeholder.value ?? '';
    const options = (): DropdownOption[] => parseFieldOptions(extraSettings()?.options.value ?? '');
    // Option ids use the index, not option.value: editor-typed values can contain spaces.
    const groupId = () => `${formId}-${eloquaName()}`;
    const hasError = () => !!errors()[eloquaName()];

    return (
        <Show when={extraSettings()}>
            <div>
                <VerticalSpace size={1} />
                <fieldset aria-describedby={errorDescribedBy(groupId(), hasError())}>
                    <legend class="heading-common heading-md">{placeholder()}</legend>
                    <ul>
                        <For each={options()}>
                            {(option, i) => (
                                <li>
                                    <Radio
                                        whenClicked={() => onRadioChange(eloquaName(), option.value)}
                                        value={option.value}
                                        readableName={option.text}
                                        isChecked={formValues()[eloquaName()] === option.value}
                                        name={'custom-radio-' + eloquaName()}
                                        id={`${groupId()}-${i()}`}
                                        hasErrors={hasError()}
                                    />
                                </li>
                            )}
                        </For>
                    </ul>
                    <Show when={hasError()}>
                        <FieldErrorMessage errors={errors} fieldKey={eloquaName()} id={fieldErrorId(groupId())} />
                    </Show>
                </fieldset>
            </div>
        </Show>
    );
};

export const CheckboxGroup = (props: FieldProps) => {
    const { formId, formValues, errors, onCheckboxChange } = props.ctx;

    const extraSettings = () => props.field.extraSettings;
    const required = () => props.field.required;
    const eloquaName = () => extraSettings()?.name.value ?? '';
    const placeholder = () => required() ? extraSettings()!.placeholder.value + '*' : extraSettings()?.placeholder.value ?? '';
    const options = (): DropdownOption[] => parseFieldOptions(extraSettings()?.options.value ?? '');
    const selectedValues = () => (formValues()[eloquaName()] ?? '').split(',').filter(Boolean);
    // Option ids use the index, not option.value: editor-typed values can contain spaces.
    const groupId = () => `${formId}-${eloquaName()}`;
    const hasError = () => !!errors()[eloquaName()];

    return (
        <Show when={extraSettings()}>
            <div>
                <VerticalSpace size={1} />
                <fieldset aria-describedby={errorDescribedBy(groupId(), hasError())}>
                    <legend class="heading-common heading-md">{placeholder()}</legend>
                    <ul>
                        <For each={options()}>
                            {(option, i) => (
                                <li>
                                    <Checkbox
                                        isChecked={selectedValues().includes(option.value)}
                                        whenClicked={(e: any) => onCheckboxChange(eloquaName(), option.value, e.target.checked)}
                                        value={option.value}
                                        name={option.text}
                                        inputName={'custom-checkbox-' + eloquaName()}
                                        id={`checkbox-${groupId()}-${i()}`}
                                        hasErrors={hasError()}
                                    />
                                </li>
                            )}
                        </For>
                    </ul>
                    <Show when={hasError()}>
                        <FieldErrorMessage errors={errors} fieldKey={eloquaName()} id={fieldErrorId(groupId())} />
                    </Show>
                </fieldset>
            </div>
        </Show>
    );
};
